import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './schedule.css';
import Icon from './icons';
import AddSheet from './AddSheet';
import EventsList from './EventsList';
import NowNextCard from './NowNextCard';
import RulesList from './RulesList';
import WeekView from './WeekView';
import { DefCard, EmptyState, ErrorState, Loading, Targets } from './States';
import { SchCtx } from './context';
import { makeFmt } from '../../../schedule/format';
import {
  EVERY_DAY, addDays, buildTally, colorSlot, formFromRule, hitsTarget, holdFromBlock, nextYearForm, nowNext, onceFinished, onceTimes, pastEvents, reuseForm, rulesForScreen,
  stableRanks, tallyNow, targetScreens, weekSegments, ymdParts,
} from '../../../schedule/model';
import HoldDialog from './HoldDialog';

const newForm = (now, kind, prefill) => ({
  step: 1, edit: null, show: null, kind: kind || null,
  days: [...EVERY_DAY], preset: 'morning', from: 360, to: 720,
  evName: '', start: addDays(now.date, 7), end: addDays(now.date, 9), allDay: true, efrom: 480, eto: 1380, tpl: null,
  where: { mode: 'all', ids: [] }, q: '', resolve: 'new',
  onow: '60', odate: now.date, ...onceTimes('60', now.min), oname: '', occ: null, occF: 'all',
  ...(prefill || {}),
});

/**
 * The new «الجدول» page (prototype `popuppush-schedule.html`), presentational: it takes the account's data and a few
 * actions and owns only the UI state (target, view, open sheet, delete confirmation, toast). The container
 * (`ScheduleV6`) wires the real API; the pixel harness mounts this with the prototype's own example data.
 *
 *   status        'ready' | 'loading' | 'error'
 *   now           { date, dow (Sunday = 0), min, ms }, in the company's time zone
 *   tz            company time zone (fallback of a screen without its own)
 *   shows         [{ id, name, hint?, slot? }]     the campaigns a rule can show (slot = fixed colour 1..7, else hashed from the id)
 *   groups        [{ id | null, name, screens: [{ id, name, tz?, version? }] }]
 *   items         rules + events in the page's shape (model.ruleFromApi)
 *   def           { show, others } | null          the standing show screens return to
 *   serverNow     { [screenId]: { cur, next } } | null   Pro: what the backend says each screen plays (status-many)
 *   pro           events unlocked
 *   minVersion    first player build that follows events ahead of time (null = unknown)
 *   oldScreens    how many of the company's recently seen screens are below it
 *   occasions     { country, items, loading, failed }   the occasions calendar (GET /campaign-schedules/occasions)
 *   actions       { save({ form, resolve }), toggle(item, on), remove(item), conflicts(form), retry(), upgrade(),
 *                   preview(where) -> { screens }, manualChange({ deviceIds, campaignId }), endNow(ruleKeys), resume(deviceIds), setCountry(code) }
 *   onTarget      (target) => void
 */
export default function ScheduleApp({
  status = 'ready', now, tz, shows, groups, items, def, serverNow = null, pro = true, minVersion = null, oldScreens = 0, occasions = { items: [] }, actions, onTarget,
}) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'ar').startsWith('en') ? 'en' : 'ar';
  const f = useMemo(() => makeFmt(t, lang), [t, lang]);

  const [target, setTarget] = useState('all');
  const [view, setView] = useState('list');
  const [confirmDel, setConfirmDel] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [pastOpen, setPastOpen] = useState(false);
  const [manual, setManual] = useState(null); // { ids, exclude, screens, loading, failed, busy }
  const toastTimer = useRef(null);
  const lastFocus = useRef(null);

  const year = ymdParts(now.date).y;
  const screens = useMemo(() => groups.flatMap((g) => g.screens.map((s) => ({ ...s, g: g.id }))), [groups]);
  const screenById = useMemo(() => new Map(screens.map((s) => [s.id, s])), [screens]);
  const showMap = useMemo(() => new Map(shows.map((s) => [s.id, s])), [shows]);
  const itemShowName = useMemo(() => new Map(items.map((i) => [i.show, i.showName])), [items]);

  const showName = useCallback((id) => showMap.get(id)?.name ?? itemShowName.get(id) ?? t('sch6.no_show'), [showMap, itemShowName, t]);
  const col = useCallback((id) => `var(--s${id ? (showMap.get(id)?.slot ?? colorSlot(id)) : 7})`, [showMap]);
  const screenName = useCallback((id) => screenById.get(id)?.name ?? '', [screenById]);
  const tzOf = useCallback((id) => screenById.get(id)?.tz || tz, [screenById, tz]);
  const whereText = useCallback((item) => f.whereTxt(item.where, { screens, groups: groups.filter((g) => g.id !== null), total: screens.length }, item.screensCount), [f, screens, groups]);

  const ctx = useMemo(() => ({ t, f, lang, groups, screens, now, year, showName, col, screenName, tzOf, whereText }), [t, f, lang, groups, screens, now, year, showName, col, screenName, tzOf, whereText]);

  useEffect(() => { if (onTarget) onTarget(target); }, [target, onTarget]);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const say = (msg) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  };

  const defShow = def?.show ?? null;
  const ids = useMemo(() => targetScreens(target, groups), [target, groups]);
  const minuteKey = Math.floor(now.ms / 60000);

  // screens whose schedule a person paused (schedule v4): from the screens list, which works on every plan, else from the
  // status answer. The two refresh at different moments right after «end now» / a manual change, so either one alone may be
  // the first to know; a screen counts as held when either says so.
  const held = useMemo(() => {
    const m = {};
    screens.forEach((sc) => {
      const h = holdFromBlock(sc.paused) || serverNow?.[sc.id]?.cur?.hold;
      if (h) m[sc.id] = { ...h, show: serverNow?.[sc.id]?.cur?.show ?? null };
    });
    return m;
  }, [screens, serverNow]);
  const rank = useMemo(() => stableRanks(groups), [groups]);

  // what each screen of the target plays now: the backend's answer when we have it (Pro), else the same engine locally
  const cardData = useMemo(() => {
    if (!ids.length) return null;
    const local = tallyNow(items, ids, groups, defShow, now.ms, tz, tzOf, held);
    const cur = (id) => (local[id].kind === 'manual' ? local[id] : (serverNow?.[id] && serverNow[id].cur.kind !== 'manual' ? serverNow[id].cur : local[id]));
    const tally = buildTally(ids, cur, rank);
    const top = tally[0];
    const rep = top.rep;
    const rtz = tzOf(rep);
    const nn = nowNext(rulesForScreen(items, rep, groups), defShow, now.ms, rtz, top.kind === 'manual' && held[rep] ? { untilMs: held[rep].until, show: top.show } : null);
    if (top.kind !== 'manual' && serverNow?.[rep] && serverNow[rep].cur.kind !== 'manual') {
      const sv = serverNow[rep];
      return { ids, tally, rep, nn: { cur: { ...sv.cur, once: sv.cur.once ?? top.once }, next: sv.next ?? null, startedMs: nn.cur.show === sv.cur.show ? nn.startedMs : null } };
    }
    return { ids, tally, rep, nn: { ...nn, cur: { ...nn.cur, ...(top.kind === 'manual' ? { kind: 'manual' } : {}), ...(top.once ? { once: true } : {}) } } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, items, groups, defShow, minuteKey, tz, tzOf, serverNow, held, rank]);

  const weekData = useMemo(() => {
    if (view !== 'week' || !ids.length) return null;
    const rep = ids[0];
    return { rep, days: weekSegments(rulesForScreen(items, rep, groups), defShow, addDays(now.date, -now.dow), tzOf(rep)) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, ids, items, groups, defShow, now.date, tzOf]);

  const mine = useMemo(() => items.filter((i) => hitsTarget(i.where, target, groups)), [items, target, groups]);
  const events = mine.filter((i) => i.type === 'event');
  const upcoming = events.filter((e) => e.end >= now.date && !onceFinished(e, now)).sort((a, b) => (a.start < b.start ? -1 : (a.start > b.start ? 1 : (a.from || 0) - (b.from || 0))));
  const past = pastEvents(events, now);
  const rules = mine.filter((i) => i.type === 'rule');

  /* ── actions ── */
  const openForm = (kind, prefill) => {
    lastFocus.current = document.activeElement;
    setForm(newForm(now, kind, prefill));
  };
  const closeForm = useCallback(() => {
    setForm(null);
    setSaving(false);
    if (lastFocus.current && lastFocus.current.focus) lastFocus.current.focus();
  }, []);
  const quick = (q) => {
    if (q === 'daily') openForm('rule', { days: [...EVERY_DAY] });
    if (q === 'once') openForm('once');
    if (q === 'event') openForm('event');
  };
  const edit = (item) => { lastFocus.current = document.activeElement; setForm({ ...newForm(now, item.type === 'event' ? (item.once ? 'once' : 'event') : 'rule'), ...formFromRule(item) }); };
  const reuse = (item) => { lastFocus.current = document.activeElement; setForm({ ...newForm(now, item.once ? 'once' : 'event'), ...reuseForm(item, now.date) }); };
  const repeat = (item) => { lastFocus.current = document.activeElement; setForm({ ...newForm(now, 'event'), ...nextYearForm(item) }); };

  // a one-time show with no name gets «show — today 10:15 AM»
  const autoName = useCallback((F) => f.onceAutoName(showName(F.show), F.odate, F.ofrom, now.date, year), [f, showName, now.date, year]);
  const finalize = useCallback((F) => (F.kind === 'once' ? { ...F, oname: (F.oname || '').trim() || autoName(F) } : F), [autoName]);

  const save = async (resolve) => {
    setSaving(true);
    const res = await actions.save({ form: finalize(form), resolve });
    setSaving(false);
    if (res?.ok) { const edited = !!form.edit; closeForm(); say(t(edited ? 'sch6.t_edited' : 'sch6.t_saved')); } else if (!res?.handled) say(t('sch6.t_error'));
  };
  const toggle = async (item) => {
    const res = await actions.toggle(item, !item.on);
    if (res?.ok) say(t(item.on ? 'sch6.t_off' : 'sch6.t_on')); else if (!res?.handled) say(t('sch6.t_error'));
  };
  const removeConfirmed = async () => {
    const cd = confirmDel;
    const res = await actions.remove(cd.item);
    setConfirmDel(null);
    if (res?.ok) say(t('sch6.t_deleted')); else if (!res?.handled) say(t('sch6.t_error'));
  };

  /* ── the now card: another campaign now, end now, resume ── */
  const topGroup = cardData?.tally[0];
  const whoText = (n) => (n >= screens.length ? t('sch6.who_all') : f.nScreens(n));
  const untilOf = (h, id) => f.untilTxt(h.untilLocal, h.untilAt, tzOf(id), now.date, year);

  const openManual = async () => {
    const gIds = topGroup.ids;
    setManual({ ids: gIds, exclude: topGroup.show, screens: [], loading: true, failed: false, busy: false });
    try {
      const res = await actions.preview({ mode: 'screens', ids: gIds });
      setManual((m) => (m ? { ...m, loading: false, screens: (res?.screens || []).filter((x) => !x.already_paused) } : m));
    } catch (e) { setManual((m) => (m ? { ...m, loading: false, failed: true } : m)); }
  };
  const confirmManual = async (showId) => {
    setManual((m) => ({ ...m, busy: true }));
    const first = manual.screens[0];
    const res = await actions.manualChange({ deviceIds: manual.ids, campaignId: showId });
    if (res?.ok) {
      const until = first ? f.untilTxt(first.until_local, first.until_at, tz, now.date, year) : '';
      setManual(null);
      say(t('sch6.t_manual', { show: showName(showId), time: until }));
    } else { setManual((m) => (m ? { ...m, busy: false } : m)); if (!res?.handled) say(t('sch6.t_error')); }
  };
  const endNowYes = async () => {
    const keys = [...topGroup.ruleIds];
    const res = await actions.endNow(keys);
    setConfirmEnd(false);
    if (res?.ok) say(t('sch6.t_ended', { name: showName(topGroup.show) })); else if (!res?.handled) say(t('sch6.t_error'));
  };
  const resumeNow = async () => {
    const res = await actions.resume(topGroup.ids);
    if (res?.ok) say(t('sch6.t_resumed')); else if (!res?.handled) say(t('sch6.t_error'));
  };
  const nowUi = topGroup && {
    confirmEnd,
    canEnd: topGroup.ruleIds.size > 0,
    onManual: openManual,
    onEnd: () => setConfirmEnd(true),
    onEndNo: () => setConfirmEnd(false),
    onEndYes: endNowYes,
    onResume: resumeNow,
    endText: t('sch6.end_confirm', { name: showName(topGroup.show), who: whoText(topGroup.ids.length), def: defShow ? showName(defShow) : t('sch6.no_show'), tail: t(topGroup.kind === 'rule' ? 'sch6.end_tail_rule' : 'sch6.end_tail_event') }),
    heldText: topGroup.kind === 'manual' && held[topGroup.rep]
      ? t('sch6.held_tail', {
        lead: t(held[topGroup.rep].reason === 'end_now' ? 'sch6.held_end' : 'sch6.held_manual', { name: held[topGroup.rep].scheduleName || '' }),
        who: whoText(topGroup.ids.length),
        until: untilOf(held[topGroup.rep], topGroup.rep),
      })
      : '',
  };

  const empty = status === 'ready' && items.length === 0;

  return (
    <SchCtx.Provider value={ctx}>
      <div className="sch6" dir={lang === 'ar' ? 'rtl' : 'ltr'} lang={lang}>
        <div className="sch6-main"><div className="sch6-main-in">
          <div className="sch6-head">
            <div><h1>{t('sch6.title')}</h1><p className="sch6-sub">{t('sch6.sub')}</p></div>
            {status === 'ready' && !empty && <button type="button" className="sch6-btn sch6-pri" onClick={() => openForm(null)}><Icon name="plus" />{t('sch6.add')}</button>}
          </div>
          {status === 'loading' && <Loading />}
          {status === 'error' && <ErrorState onRetry={actions.retry} />}
          {empty && <EmptyState def={def} onQuick={quick} />}
          {status === 'ready' && !empty && (
            <>
              {oldScreens > 0 && <p className="sch6-note sch6-warnnote"><Icon name="alert" /><span>{t('sch6.old_ver_page', { n: f.num(oldScreens) })}</span></p>}
              <Targets target={target} onTarget={(x) => { setTarget(x); setConfirmDel(null); setConfirmEnd(false); }} />
              {cardData && <NowNextCard data={cardData} ui={nowUi} />}
              <EventsList
                upcoming={upcoming}
                past={past}
                pastOpen={pastOpen}
                onPastToggle={() => { setPastOpen((o) => !o); setConfirmDel(null); }}
                onReuse={reuse}
                confirmDel={confirmDel && confirmDel.kind === 'event' ? confirmDel : null}
                onAdd={() => openForm('event')}
                onEdit={edit}
                onDelete={(e) => setConfirmDel({ kind: 'event', id: e.id, item: e })}
                onRepeat={repeat}
                onNo={() => setConfirmDel(null)}
                onYes={removeConfirmed}
              />
              <RulesList
                list={rules}
                view={view}
                onView={setView}
                week={weekData && <WeekView days={weekData.days} previewName={screenName(weekData.rep)} showPreview={ids.length > 1} />}
                confirmDel={confirmDel && confirmDel.kind === 'rule' ? confirmDel : null}
                onAdd={() => openForm('rule')}
                onToggle={toggle}
                onEdit={edit}
                onDelete={(r) => setConfirmDel({ kind: 'rule', id: r.id, item: r })}
                onNo={() => setConfirmDel(null)}
                onYes={removeConfirmed}
              />
              <DefCard def={def} />
            </>
          )}
        </div></div>
        {form && (
          <AddSheet
            F={form}
            set={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
            onClose={closeForm}
            onSave={save}
            saving={saving}
            checkConflicts={(F) => actions.conflicts(finalize(F))}
            occasions={occasions}
            onCountry={(c) => { actions.setCountry(c); setForm((prev) => ({ ...prev, occ: null })); }}
            autoName={autoName}
            shows={shows}
            pro={pro}
            onUpgrade={actions.upgrade}
            minVersion={minVersion}
          />
        )}
        {manual && (
          <HoldDialog
            pick
            screens={manual.screens}
            total={screens.length}
            shows={shows}
            exclude={manual.exclude}
            tz={tz}
            today={now.date}
            loading={manual.loading}
            failed={manual.failed}
            busy={manual.busy}
            onConfirm={confirmManual}
            onClose={() => setManual(null)}
          />
        )}
        <div aria-live="polite">{toast && <div className="sch6-toast"><Icon name="check" />{toast}</div>}</div>
      </div>
    </SchCtx.Provider>
  );
}
